import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { CommonModal } from 'wsm-common-modal';
import { setClassNames } from '@ddc/classnames-js';

import { isBrowser } from 'ws-scripts/modules/environment';
import { useSitemap, useRequestData } from 'wsm-common-data';

import useUdsLeadGate from '../hooks/useUdsLeadGate';

import SpotIcon from './SpotIcon';
import ReturnsItem from './ReturnsItem';
import WarrantyModalContent from './WarrantyModalContent';

import { PROTECTION_PLAN_ROUTE } from '../constants';

import './Item.scss';

const Item = ({
	iconName,
	title,
	content,
	linkUrl,
	linkText,
	opensModal,
	modalContent,
	openSdpRoute,
	badgeClasses = [],
}) => {
	const sitemap = useSitemap();
	const { deviceType } = useRequestData();

	const [paymentsInfo, setPaymentsInfo] = useState({});
	const isLeadGateActive = useUdsLeadGate();

	const isMobile = deviceType === 'MOBILE';

	const isContentArray = Array.isArray(content);
	let contentAsList = '';
	if (isContentArray) {
		contentAsList = content.map((detail) => {
			return <li key={detail}>{detail}</li>;
		});
	}
	const isImg = content && iconName.includes('.svg');
	const isIcon = content && !isImg;
	const isEmptyContent = isContentArray
		? contentAsList.length === 0
		: !content;

	const isURL = (str) => {
		const pattern = new RegExp(
			'^(https?:\\/\\/)?' + // protocol
				'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
				'((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
				'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
				'(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
				'(\\#[-a-z\\d_]*)?$',
			'i'
		); // fragment locator
		return pattern.test(str);
	};

	let resolvedUrl = linkUrl;
	if (resolvedUrl) {
		if (isURL(linkUrl)) {
			// Full URL - use as-is
			resolvedUrl = linkUrl;
		} else if (linkUrl.startsWith('/')) {
			// Page path - use as-is (relative paths work directly)
			resolvedUrl = linkUrl;
		} else {
			// Page alias - lookup in sitemap
			resolvedUrl = sitemap?.get(linkUrl) || linkUrl;
		}
	}

	const [isOpenModal, setOpenModal] = useState(false);
	const toggleOpenModal = () => {
		setOpenModal((previous) => !previous);
	};

	const onButtonClick = () => {
		if (isBrowser && openSdpRoute && window.caiEventBus) {
			const payments = (paymentsInfo.dealType) ? {
				paymentsInfo: {
					...paymentsInfo,
					...(paymentsInfo.dealType === 'lease' && {
						leaseMileageRange: paymentsInfo.annualMiles,
					}),
				}
			} : {};

			window.caiEventBus.emit('uds:openSdp:request', {
				route: openSdpRoute,
				...payments
			});

		}
		if (opensModal && modalContent) {
			toggleOpenModal();
		}
	};

	let isReturns = false;
	if (iconName === 'returns') {
		isReturns = true;
	}

	const isAutoCheck = badgeClasses.includes('autocheck');
	const autoCheckClassNames = isAutoCheck ? badgeClasses.join(' ') : null;
	const logoSize = isAutoCheck ? 80 : 64;

	const iconAutoCheckClasses = setClassNames([
		'ddc-icon',
		'ddc-icon-launch',
		isMobile ? 'ddc-font-size-xxlarge' : 'ddc-font-size-xlarge',
		'text-muted',
		'ml-3'
	]);

	// Hide "Review protection plans" button if UDS Lead Gate is active
	const shouldShowAsButton = (
		(opensModal && openSdpRoute !== PROTECTION_PLAN_ROUTE) ||
		(opensModal && openSdpRoute === PROTECTION_PLAN_ROUTE && !isLeadGateActive)
	);

	const pubsub = isBrowser && window?.DDC?.pubsub;
	useEffect(() => {
		if (pubsub) {
			pubsub.subscribe('ws-vehicle-confidence/paymentsInfo', data => {
				setPaymentsInfo(data);
			});
		}

		return () => {
			pubsub.unsubscribe('ws-vehicle-confidence/paymentsInfo');
		};
	}, [pubsub])

	const linkElement = isAutoCheck ? (
		<a
			className={`autocheck-link d-inline-flex align-items-center text-decoration-none text-muted ${autoCheckClassNames}`}
			href={linkUrl}
		>
			<span className="text-decoration-underline text-link text-link-muted">
				{linkText}
			</span>
			<i
				data-testid="text-icon"
				aria-hidden="true"
				className={iconAutoCheckClasses}
			/>
		</a>
	) : (
		<a
			className="text-link-muted text-decoration-underline"
			href={resolvedUrl}
		>
			{linkText}
		</a>
	);

	if (!isEmptyContent) {
		return !isReturns ? (
			<div className="d-flex align-items-start my-3 col-sm-12 col-md-6">
				<div className="mr-3">
					{isIcon ? (
						<SpotIcon
							showIcon
							spotIconName={iconName}
							spotIconSize={64}
						/>
					) : (
						isImg && (
							<img
								src={iconName}
								width={logoSize}
								height={logoSize}
								alt="vehicle history report"
							/>
						)
					)}
				</div>
				<div>
					<h4>{title}</h4>
					<div className="mt-1 mb-3">
						{isContentArray && <ul>{contentAsList}</ul>}
						{!isContentArray && content}
					</div>
					{shouldShowAsButton
						? (
							<button
								className="text-link text-link-muted text-decoration-underline"
								onClick={onButtonClick}
								type="button"
							>
								{linkText}
							</button>
						) : (
							linkUrl && linkElement
						)}
					{opensModal && modalContent && (
						<CommonModal
							title={title}
							isOpen={isOpenModal}
							onClose={toggleOpenModal}
						>
							<WarrantyModalContent
								warrantyItems={modalContent}
								closeModal={toggleOpenModal}
							/>
						</CommonModal>
					)}
				</div>
			</div>
		) : (
			<ReturnsItem
				title={title}
				content={content}
				opensModal={opensModal}
				linkText={linkText}
				modalContent={modalContent}
			/>
		);
	} else {
		return null;
	}
};
Item.propTypes = {
	iconName: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	content: PropTypes.oneOfType([PropTypes.element, PropTypes.string])
		.isRequired,
	linkUrl: PropTypes.string,
	linkText: PropTypes.string,
	opensModal: PropTypes.bool,
	modalContent: PropTypes.element,
	openSdpRoute: PropTypes.string,
	badgeClasses: PropTypes.arrayOf(PropTypes.shape({})),
};
export default Item;
